import type { Software, ModuleUsagePrice } from '@repositories/softwareRepository/software.types';
import type { ContractCondition, SubscriptionSnapshot, SubscriptionType } from '@repositories/subscriptionRepository/subscription.types';
import type { RealtimeUsage } from '@repositories/usage';
import { ProductPlanModel } from '@repositories/softwareRepository/models/ProductPlanModel';

export class SubscriptionSnapshotModel {
  private readonly _subscriptionId: string;
  private readonly _subscriptionVersion: number;
  private readonly _parentId: string;
  private readonly _tenantId: string;
  private readonly _software: Software;
  private readonly _softwareContractId: string;
  private readonly _softwareId: string;
  private readonly _contractId: string;
  private readonly _type: SubscriptionType;
  private readonly _name: string;
  private readonly _description: string;
  private readonly _startDate: string;
  private readonly _endDate: string;
  private readonly _timeZone: string;
  private readonly _contractConditions: ContractCondition[];
  private readonly _productPlan: ProductPlanModel;
  private readonly _moduleUsagePrices: ModuleUsagePrice[];
  private readonly _currencyUnit: CurrencyUnit;
  private readonly _properties: { [key: string]: string };
  private readonly _realtimeUsage: RealtimeUsage;

  constructor(dto?: SubscriptionSnapshot) {
    this._subscriptionId = dto?.subscriptionId || '';
    this._subscriptionVersion = dto?.subscriptionVersion || 0;
    this._parentId = dto?.parentId || '';
    this._tenantId = dto?.tenantId || '';
    this._software = dto?.software ?? { id: '', productCode: '', name: '', planVisibilityScope: 'ALL_USERS' };
    this._softwareContractId = dto?.softwareContractId ?? '';
    this._softwareId = dto?.softwareId || '';
    this._contractId = dto?.contractId || '';
    this._type = dto?.type || 'ON_DEMAND';
    this._name = dto?.name || '';
    this._description = dto?.description || '';
    this._startDate = dto?.startDate || '';
    this._endDate = dto?.endDate || '';
    this._productPlan = new ProductPlanModel(dto?.productPlan);
    this._timeZone = dto?.timeZone || '';
    this._contractConditions = dto?.contractConditions || [];
    this._moduleUsagePrices = dto?.moduleUsagePrices || [];
    this._currencyUnit = dto?.currencyUnit || 'KRW';
    this._properties = dto?.properties || {};
    this._realtimeUsage = dto?.realtimeUsage || { dateTime: '', subscriptionId: '', usageList: [] };
  }

  get productPlan() {
    return this._productPlan;
  }

  get subscriptionId() {
    return this._subscriptionId;
  }

  get subscriptionVersion() {
    return this._subscriptionVersion;
  }

  get parentId() {
    return this._parentId;
  }

  get tenantId() {
    return this._tenantId;
  }

  get software() {
    return this._software;
  }

  get softwareContractId() {
    return this._softwareContractId;
  }

  get softwareId() {
    return this._softwareId;
  }

  get contractId() {
    return this._contractId;
  }

  get type() {
    return this._type;
  }

  get name() {
    return this._name;
  }

  get description() {
    return this._description;
  }

  get startDate() {
    return this._startDate;
  }

  get endDate() {
    return this._endDate;
  }

  get timeZone() {
    return this._timeZone;
  }

  get contractConditions() {
    return this._contractConditions;
  }

  get moduleUsagePrices() {
    return this._moduleUsagePrices;
  }

  get currencyUnit() {
    return this._currencyUnit;
  }

  get properties() {
    return this._properties;
  }

  get realtimeUsage() {
    return this._realtimeUsage;
  }

  get isExternalSubscription() {
    return this._type === 'EXTERNAL';
  }
}
